import * as React from "react"

import {DatePicker} from "../sbhtml/datePicker"
import {mapValues} from "../utils";

import {
	addCallbacks, CheckboxFilterProps, filterIsEnabled, Filters, FilterWidgetCheckboxItem, FilterProps,
	FilterWidgetValueParams, ReportTypeData, ReportTypes, ReportProps
} from "./lib2";
import {Axes, DisplayModes, RenderSelectMode} from "./graph-renderer";

const colors = [
	"#a6cee3",
	"#1f78b4",
	"#b2df8a",
	"#33a02c",
	"#fb9a99",
	"#e31a1c",
	"#fdbf6f",
	"#ff7f00",
	"#cab2d6",
	"#6a3d9a",
	"#ffff99",
	"#b15928",
]
const filter: React.CSSProperties = {
	margin: "5px",
	padding: "5px",
	backgroundColor: "#DEDEDE"
}

const makeReportTypeOption = <AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>>(s: ReportTypeData<AA,FF,DD>, id: keyof RR) => {
	return <option key={id} value={id}>{s.label}</option>
}

export const Report = <AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>>(props: ReportProps<AA, FF, DD, RR>) => {
	const fillColors = colors.slice()
	const borderColors = colors.slice()

	for(let i=0; i < colors.length; i++) {
		for(let j=0; j < colors.length; j++) {
			if(colors[i] != colors[j]) {
				borderColors.push(colors[i])
				fillColors.push(colors[j])
			}
		}
	}

	// noinspection JSUnusedLocalSymbols
	const ChartRenderer = props.displayModes[props.displayMode]
	const report = props.reportTypes[props.reportType]
	const chart = props.chartData ? <ChartRenderer borderColors={borderColors} fillColors={fillColors} axis={props.axes[report.axis]} chartData={props.chartData} removeEmptyAxes={report.removeEmptyAxes} keys={report.keys}/> : <div>No data</div>

	const filters = Object.keys(props.filters).filter(f => !props.filters[f].isHidden).map((f): [FilterWidgetValueParams, string] => [props.filters[f], f])
	const hasFilters = filters.length > 0
	const width = hasFilters ? 260 : 10
	const display = !hasFilters ? {display: 'none'} : {}

	const reportTypeOnChange:React.ChangeEventHandler<HTMLSelectElement> = (e) => {
		const newReportType = e.currentTarget.value
		const newReport = props.reportTypes[newReportType]
		if(newReport.displayModes.indexOf(props.displayMode) < 0) {
			props.setDisplay(newReport.defaultDisplayMode)
		}
		props.onSetReportType(newReportType)
		props.runReport()
	}

	return <div style={{overflow: "auto"}}>
		<h1>{props.title}</h1>
		<div style={{float: "right", width: width, ...display}}>
			<div>
				{filters.map(
					([a, n]) => <div key={n} style={filter}>
						<RenderFilter id={n} data={addCallbacks(a, props.filterCallbacks, props.filterState[n])} enabled={filterIsEnabled(n, report)} key={n} />
					</div>
				)}
			</div>
			<button onClick={props.runReport}>Run report</button>
		</div>
		<div style={{marginRight: width + 10}}>
			<div>
				<select style={{width: "25%"}} value={props.reportType} onChange={reportTypeOnChange}>
					{mapValues<ReportTypeData<AA,FF,DD>,JSX.Element,RR>(props.reportTypes, makeReportTypeOption)}
				</select>
				<span style={{paddingLeft: "1em"}}>
					<RenderSelectMode modes={report.displayModes} mode={props.displayMode} setMode={props.setDisplay}/>
				</span>
				<h2 style={{textTransform: "uppercase", float: "right"}}>{report.label}</h2>
			</div>
			<div style={{height: "100%", width: "100%"}}>{chart}</div>
		</div>
	</div>
}

const RenderFilter = <FF extends Filters>(props: FilterProps<FF>): JSX.Element => {
	const f = props.data
	switch (f.type) {
		case "select box":
			return <select disabled={!props.enabled} style={{width: "100%"}} value={f.selected} onChange={(e) => f.onSelect(e.currentTarget.value, props.id)}>
				{mapValues(f.options, (a, b) => <option key={b} value={b}>{a}</option>)}
			</select>
		case "date range":
			const renderDate = (d: Date, onchange: (s: string, d: Date) => void, invalid: boolean) => props.enabled ? <span><DatePicker
				disabled={!props.enabled}
				onChange={onchange}
				date={d}
				readonly={!props.enabled}
				invalid={invalid} /></span> : <input disabled={!props.enabled} value={d.getDate() + "/" + (d.getMonth()+1) + "/" + d.getFullYear()}/>
			return <dl>
				<dt>From</dt>
				<dd>
					{renderDate(f.fromDate, (s, d) => f.onSetFromDate(d, props.id), f.fromDate > f.toDate)}
				</dd>
				<dt>To</dt>
				<dd>
					{renderDate(f.toDate, (s, d) => f.onSetToDate(d, props.id), f.toDate > new Date())}
				</dd>
			</dl>
		case "checkbox":
			return <CheckboxFilter
				id={props.id}
				enabled={props.enabled}
				onSetFilter={f.onSetCheckbox}
				onUnsetFilter={f.onUnset}
				label={f.label}
				options={f.group}
				checked={f.checked}
			/>
	}
}

const CheckboxFilter = <FF extends Filters>(props: CheckboxFilterProps<FF>) =>
	<div style={filter}>
		<dl style={props.enabled ? {} : {opacity: 75}}>
			<dt>{props.label}</dt>
			<dd style={{margin: 0}}><ul style={{height: "7em", overflow: "auto", border: "solid black 1px", backgroundColor: props.enabled ? "white" : "lightgray" , opacity: props.enabled ? 100 : 75}}>
				{props.options.map(renderCheckbox(props))}
			</ul></dd>
		</dl>
	</div>

const renderCheckbox = <FF extends Filters>(props: CheckboxFilterProps<FF>) => (e: FilterWidgetCheckboxItem) => {
	const checked = props.checked.indexOf(e.value) >= 0
	return <li key={e.value}>
		<label>
			<input
				disabled={!props.enabled}
				type="checkbox"
				value={e.value}
				onChange={() => (checked ? props.onUnsetFilter : props.onSetFilter)(props.id, e.value)}
				checked={checked}
			/>{e.label}</label>
	</li>
}



// WEBPACK FOOTER //
// ./src/lib/report-builder/lib1.tsx